import { ReactNode, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { FormProvider, useController, useForm } from "react-hook-form";
import { TPopulatedForm, useUploadFile } from "../../hooks/applications.hooks";
import { cleanseObject, openInNewTab } from "../../utils/common";
import { formatDate } from "../../utils/date";
import Input from "../CommonComponents/Input/Input";
import { tableCustomStyles } from "../CommonComponents/Table/Table";
import { RenderField } from "../RenderField/RenderField";
import { ErrorToast } from "../toastMessage";
import {
  DeleteButton,
  ViewButton,
} from "../CommonComponents/EditComponent/EditComponent";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { DocumentPreviewDialog } from "../CommonComponents/DialogConfirmation/DocPreviewDialog";
import {
  useGetFieldLabel,
  useGetFieldPlaceholder,
} from "../../contexts/LanguageContext";

export function RenderNOC({
  step,
  children,
  defaultValues,
}: {
  defaultValues?: Record<string, any>;
  children: (_: ReturnType<typeof useForm>) => ReactNode | ReactNode[];
  step: TPopulatedForm;
}) {
  const convert = useGetFieldLabel();
  const convertPlaceholder = useGetFieldPlaceholder();
  const uploadMutation = useUploadFile();
  const [inc, setInc] = useState(1);
  const formMethods = useForm<Record<string, unknown>>({
    defaultValues: {
      nocs: defaultValues?.nocs || [],
      ...(step.fields || [])?.reduce((acc, e) => ({ ...acc, [e.uid]: "" }), {}),
    },
    mode: "onChange",
  });
  const [previewNocDocument, setPreviewNocDocument] = useState<{
    url: string;
  } | null>(null);
  const { control, trigger, formState, setError, getValues, resetField } =
    formMethods;
  const { isValid } = formState;

  const nocDocument = useController({ control, name: "noc_document" });

  const uploadedNOC = useController({
    control,
    name: "nocs",
    defaultValue: defaultValues?.nocs || [],
  });

  const { onChange: setUploadedNOC, value: uploadedNOCValue } =
    uploadedNOC.field;

  const onUpload = async () => {
    try {
      if (!isValid) return trigger();

      const { nocs: _, noc_document, ...rest } = getValues();
      if (noc_document instanceof File) {
        const result = await uploadMutation.mutateAsync(noc_document);
        const noc_doc = result?.data?.data;
        if (!noc_doc) throw new Error();

        const newVal = [
          ...((uploadedNOCValue as []) || []),
          { ...cleanseObject(rest), noc_doc },
        ];

        setUploadedNOC(newVal);
        nocDocument.field.onChange(null);
        setInc((e) => e + 1);
        (step.fields || []).forEach((e) =>
          resetField(e.uid, { defaultValue: "" })
        );
      } else {
        setError("noc_document", { message: "* Required" });
        ErrorToast("Select a file to upload!");
      }
    } catch (err) {
      ErrorToast("Something went wrong while uploading file!");
    }
  };

  const columns = useMemo(
    (): TableColumn<Record<string, unknown>>[] => [
      {
        name: "Sr. No.",
        selector: (_, i) => (i || 0) + 1,
        center: true,
        grow: 0.5,
      },
      {
        name: "NOC Type",
        selector: (d) =>
          String(
            (
              step.fields?.find((e) => e.uid === "noc_type")?.options || []
            ).find((e) => e.id === d.noc_type)?.name || "-"
          ),
        center: true,
        grow: 3,
      },
      {
        name: "NOC No",
        selector: (d) => String(d?.noc_number || "-"),
        center: true,
        grow: 2,
      },
      {
        name: "NOC Date",
        selector: (d) => formatDate(String(d?.noc_date || "x")),
        center: true,
        grow: 2,
        width: "135px",
      },
      {
        name: "Edit",
        width: "150px",
        center: true,
        cell: (d, i) => (
          <div className="space-x-4">
            {/* <button
              className="text-lg"
              onClick={() => openInNewTab(String(d?.noc_doc))}
            >
              <i className="m-0 fa fa-eye"></i>
            </button>
            <button
              className="text-lg"
              onClick={() =>
                setUploadedNOC(
                  (uploadedNOCValue as []).filter((_, index) => index !== i)
                )
              }
            >
              <i className="m-0 fa fa-trash"></i>
            </button> */}
            <ViewButton
              onClick={() => setPreviewNocDocument({ url: String(d?.noc_doc) })}
            />
            <DeleteButton
              onClick={() =>
                setUploadedNOC(
                  (uploadedNOCValue as []).filter((_, index) => index !== i)
                )
              }
            />
          </div>
        ),
      },
    ],
    [step, uploadedNOCValue, setUploadedNOC]
  );

  const onSelectFile = (file: File) => {
    const configToUse = step.documentConfigs?.[0];

    if (configToUse?.file_type) {
      const types = configToUse?.file_type.split(",").filter((e) => !!e);
      if (!types.some((e) => file.type.includes(e))) {
        return ErrorToast(
          "Invalid file select, please select a different file!"
        );
      }
    }
    if (
      configToUse?.file_size &&
      ["KB", "MB"].includes(configToUse?.file_size_unit || "")
    ) {
      let size = +configToUse?.file_size;
      const sizeInMB = file.size / (1024 * 1024);
      if (configToUse?.file_size_unit === "KB") {
        size /= 1024;
      }

      if (sizeInMB > size) {
        return ErrorToast(
          `Please select a small file, file cannot be more than ${configToUse.file_size} ${configToUse.file_size_unit}`
        );
      }
    }
    nocDocument.field.onChange(file);
  };

  const fields = (step?.fields || []).filter((e) => e?.uid !== "noc_document");
  const nocDocumentField = (step?.fields || []).find(
    (e) => e.uid === "noc_document"
  );

  return (
    <FormProvider {...formMethods}>
      <form
        noValidate
        id="add-application-form"
        onSubmit={formMethods.handleSubmit(() => null)}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-10">
          {fields?.map((field) => (
            <div key={`field-${field.id}`}>
              <RenderField field={field} />
            </div>
          ))}
          {!!nocDocumentField && (
            <Input
              type="file"
              key={`noc-doc-${inc}`}
              label={convert(nocDocumentField)}
              placeholder={convertPlaceholder(nocDocumentField)}
              onChange={(e) => {
                const file = e.target?.files?.[0];
                if (file) onSelectFile(file);
              }}
              ref={nocDocument.field.ref}
              onBlur={nocDocument.field.onBlur}
              error={!!nocDocument.formState?.errors?.noc_document}
              helperText={nocDocument.formState?.errors?.noc_document?.message?.toString()}
              className="file:-my-3 file:-ml-4 file:h-[50px] file:border-0 file:mr-4 file:bg-primary file:px-4"
            />
          )}
        </div>
        <div className="flex justify-center items-center my-8">
          <button
            type="button"
            onClick={onUpload}
            className="bg-primary px-6 py-2 rounded-full font-bold text-white"
          >
            Upload NOC
          </button>
        </div>
        {!!(uploadedNOC.field.value as [])?.length && (
          <div className="mx-auto max-w-[800px]">
            <DataTable
              keyField="url"
              className="mt-4"
              columns={columns}
              customStyles={tableCustomStyles}
              data={
                (uploadedNOC.field.value as Record<string, unknown>[]) || []
              }
            />
          </div>
        )}

        {children(formMethods)}

        <DocumentPreviewDialog
          onClose={() => setPreviewNocDocument(null)}
          open={!!previewNocDocument}
        >
          {previewNocDocument &&
            previewNocDocument.url &&
            (previewNocDocument.url.endsWith("pdf") ? (
              <Worker
                workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}
              >
                <Viewer fileUrl={previewNocDocument.url} />
              </Worker>
            ) : (
              <img
                src={previewNocDocument.url}
                className="object-contain w-full h-full"
                alt="NOC Document"
              />
            ))}
        </DocumentPreviewDialog>
      </form>
    </FormProvider>
  );
}
